import {
  Box,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  BoxProps,
  Icon,
  IconButton,
  ButtonGroup,
  Button,
  useToast,
} from '@chakra-ui/react';
import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { RiAddLine, RiDeleteBinLine, RiEditLine } from 'react-icons/ri';
import { ConfirmationModal } from '../../../../../../../../../../../components/ConfirmationModal';
import { useAuth } from '../../../../../../../../../../../hooks/auth';
import { useVenture } from '../../../../../../../../../../../hooks/venture';
import { IVentureExperienceLevelBase } from '../../../../../../../../../../../models/ventureExperienceLevels';
import { deleteVentureExperienceLevelsService } from '../../../../../../../../../../../services/Ventures/DeleteVentureExperienceLevelsService';
import { listVentureExperienceLevelsService } from '../../../../../../../../../../../services/Ventures/ListVentureExperienceLevelsService';
import { translateError } from '../../../../../../../../../../../utils/errors';
import { HandleVentureExperienceLevelModal } from './components/HandleVentureExperienceLevelModal';

export const VentureExperienceLevelTable = (props: BoxProps): JSX.Element => {
  const toast = useToast();

  const { assignment: authenticatedAssignment } = useAuth();

  const { venture } = useVenture();

  const [ventureExperienceLevels, setVentureExperienceLevels] = useState<
    IVentureExperienceLevelBase[]
  >([]);

  const [handlingVentureExperienceLevel, setHandlingVentureExperienceLevel] =
    useState<IVentureExperienceLevelBase>();
  const [
    isDeleteConfirmationModalVisible,
    setIsDeleteConfirmationModalVisible,
  ] = useState(false);
  const [
    isHandleVentureExperienceLevelModalVisible,
    setIsHandleVentureExperienceLevelModalVisible,
  ] = useState(false);

  const toggleDeleteConfirmationModal = useCallback(
    (experienceLevel?: IVentureExperienceLevelBase) => {
      setHandlingVentureExperienceLevel(experienceLevel);
      setIsDeleteConfirmationModalVisible((prevState) => !prevState);
    },
    [],
  );

  const toggleHandleVentureExperienceLevelModal = useCallback(
    (experienceLevel?: IVentureExperienceLevelBase) => {
      setHandlingVentureExperienceLevel(experienceLevel);
      setIsHandleVentureExperienceLevelModalVisible((prevState) => !prevState);
    },
    [],
  );

  const loadVentureExperienceLevels = useCallback(
    async (ventureId: string): Promise<void> => {
      const experienceLevels = await listVentureExperienceLevelsService(
        ventureId,
      );

      setVentureExperienceLevels(experienceLevels);
    },
    [],
  );

  useEffect(() => {
    if (venture) {
      loadVentureExperienceLevels(venture.id);
    }
  }, [loadVentureExperienceLevels, venture]);

  const handleUpdateventureExperienceLevel = (): void => {
    toggleHandleVentureExperienceLevelModal(undefined);

    if (venture) {
      loadVentureExperienceLevels(venture.id);
    }
  };

  const handleDeleteVentureExperienceLevel = useCallback(
    async (experienceLevelId: string) => {
      if (venture) {
        try {
          await deleteVentureExperienceLevelsService(experienceLevelId);

          setHandlingVentureExperienceLevel(undefined);

          setVentureExperienceLevels((prevState) =>
            prevState.filter(
              (experienceLevel) => experienceLevel.id !== experienceLevelId,
            ),
          );

          toast({
            title: 'Excluído com sucesso',
            description: 'O nível de experiência foi excluído corretamente.',
            status: 'success',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        } catch (err) {
          if (axios.isAxiosError(err) && err.response?.status !== 401) {
            toast({
              title: 'Falha ao excluir',
              description:
                translateError({ message: err.response?.data.message }) ||
                'Ocorreu um erro ao excluir o nível de experiência, tente novamente.',
              status: 'error',
              duration: 3000,
              isClosable: true,
              variant: 'subtle',
              position: 'top-right',
            });
          }
        }
      }
    },
    [toast, venture],
  );

  return (
    <Box {...props}>
      {handlingVentureExperienceLevel && (
        <ConfirmationModal
          isOpen={isDeleteConfirmationModalVisible}
          onClose={() => {
            toggleDeleteConfirmationModal(undefined);
          }}
          onConfirm={() =>
            handleDeleteVentureExperienceLevel(
              handlingVentureExperienceLevel.id,
            )
          }
          title="Confirmar exclusão"
          message="Deseja realmente excluir?"
        />
      )}

      <HandleVentureExperienceLevelModal
        ventureExperienceLevel={handlingVentureExperienceLevel}
        isOpen={isHandleVentureExperienceLevelModalVisible}
        onClose={() => {
          toggleHandleVentureExperienceLevelModal(undefined);
        }}
        onSave={handleUpdateventureExperienceLevel}
      />

      {authenticatedAssignment?.featureGroup.features.some((feature) =>
        [
          'VENTURE_EXPERIENCE_LEVEL_FULL_ACCESS',
          'VENTURE_EXPERIENCE_LEVEL_WRITE_ACCESS',
        ].includes(feature.key),
      ) && (
        <ButtonGroup width="100%" justifyContent="flex-end">
          <Button
            colorScheme="green"
            size="sm"
            leftIcon={<Icon as={RiAddLine} fontSize="16" />}
            onClick={() => {
              toggleHandleVentureExperienceLevelModal(undefined);
            }}
          >
            Cadastrar novo
          </Button>
        </ButtonGroup>
      )}

      <Table colorScheme="blue" mt="4">
        <Thead>
          <Tr>
            <Th>Título</Th>
            <Th w="72" textAlign="center">
              Nível
            </Th>
            <Th w="72" textAlign="center">
              Nível mínimo de evento permitido
            </Th>
            <Th w="72" textAlign="center">
              Nível máximo de evento permitido
            </Th>
            <Th textAlign="right" w="8" />
          </Tr>
        </Thead>

        <Tbody>
          {ventureExperienceLevels.map((experienceLevel) => (
            <Tr key={experienceLevel.id}>
              <Td>{experienceLevel.title}</Td>

              <Td textAlign="center">{experienceLevel.level}</Td>

              <Td textAlign="center">
                {experienceLevel.minAllowedBookingExperience}
              </Td>

              <Td textAlign="center">
                {experienceLevel.maxAllowedBookingExperience}
              </Td>

              <Td textAlign="right">
                <ButtonGroup>
                  {authenticatedAssignment?.featureGroup.features.some(
                    (feature) =>
                      [
                        'VENTURE_EXPERIENCE_LEVEL_FULL_ACCESS',
                        'VENTURE_EXPERIENCE_LEVEL_WRITE_ACCESS',
                      ].includes(feature.key),
                  ) && (
                    <IconButton
                      aria-label="Edit"
                      size="sm"
                      icon={<Icon as={RiEditLine} />}
                      fontSize="16"
                      color="white"
                      colorScheme="yellow"
                      onClick={() =>
                        toggleHandleVentureExperienceLevelModal(experienceLevel)
                      }
                    />
                  )}

                  {authenticatedAssignment?.featureGroup.features.some(
                    (feature) =>
                      [
                        'VENTURE_EXPERIENCE_LEVEL_FULL_ACCESS',
                        'VENTURE_EXPERIENCE_LEVEL_DELETE_ACCESS',
                      ].includes(feature.key),
                  ) && (
                    <IconButton
                      aria-label="Delete"
                      size="sm"
                      icon={<Icon as={RiDeleteBinLine} />}
                      fontSize="16"
                      colorScheme="red"
                      onClick={() =>
                        toggleDeleteConfirmationModal(experienceLevel)
                      }
                    />
                  )}
                </ButtonGroup>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};
